import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor } from "lucide-react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { useConfirm } from "../../lib/confirm";
import { apiErrorMessage } from "../../lib/errors";
import { EmptyState } from "../../components/empty-state";

interface Hook {
  id: string;
  source: "mined" | "manual" | "preset";
  title: string;
  template: string;
  example: string | null;
  source_post_id: string | null;
  source_likes: number | null;
  cluster: string | null;
  use_count: number;
  starred: number;
  created_at: number;
  updated_at: number;
}

interface MiningRun {
  id: string;
  status: "pending" | "success" | "failed";
  hooks_added: number;
  source_posts_count: number;
  error_message: string | null;
  started_at: number;
  completed_at: number | null;
}

interface VoiceProfile {
  id: string;
  name: string;
  status: string;
}

const CLUSTER_LABELS: Record<string, string> = {
  contrarian: "Contrarian",
  listicle: "Listicle",
  story: "Story",
  question: "Question",
  stat: "Stat",
  declarative: "Declarative",
  promise: "Promise",
};

const CLUSTER_COLOR: Record<string, string> = {
  contrarian: "bg-rose-100 text-rose-800",
  listicle: "bg-blue-100 text-blue-800",
  story: "bg-amber-100 text-amber-800",
  question: "bg-purple-100 text-purple-800",
  stat: "bg-green-100 text-green-800",
  declarative: "bg-ink-100 text-ink-800",
  promise: "bg-cyan-100 text-cyan-800",
};

export default function HooksPage() {
  const toast = useToast();
  const confirm = useConfirm();
  const [hooks, setHooks] = useState<Hook[]>([]);
  const [latestRun, setLatestRun] = useState<MiningRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [mining, setMining] = useState(false);
  const [clusterFilter, setClusterFilter] = useState<string>("");
  const [applyingHook, setApplyingHook] = useState<Hook | null>(null);
  const [editingHook, setEditingHook] = useState<Hook | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const params = clusterFilter ? `?cluster=${clusterFilter}` : "";
      const [h, r] = await Promise.all([
        api.get<{ hooks: Hook[] }>(`/api/hooks${params}`),
        api.get<{ runs: MiningRun[] }>("/api/hooks/mining-runs"),
      ]);
      setHooks(h.hooks ?? []);
      setLatestRun(r.runs?.[0] ?? null);
      setMining(r.runs?.[0]?.status === "pending");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterFilter]);

  useEffect(() => {
    if (!mining) return;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mining]);

  const clusters = useMemo(() => {
    const set = new Set<string>();
    hooks.forEach((h) => {
      if (h.cluster) set.add(h.cluster);
    });
    return Array.from(set).sort();
  }, [hooks]);

  async function startMining() {
    setMining(true);
    try {
      await api.post("/api/hooks/mine");
      toast.success("Mining started…");
      await load();
    } catch (err) {
      setMining(false);
      toast.error(apiErrorMessage(err));
    }
  }

  async function toggleStar(h: Hook) {
    try {
      await api.patch(`/api/hooks/${h.id}`, { starred: h.starred ? 0 : 1 });
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  async function remove(h: Hook) {
    const ok = await confirm({
      title: "Delete hook",
      message: `Delete hook "${h.title}"? This can't be undone.`,
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.del(`/api/hooks/${h.id}`);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="page-title">Tweet hooks</h1>
          <p className="page-subtitle">
            AI-mined hook templates from your top-performing posts. Apply any
            template to a new topic to instantly draft variants in your voice.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowCreate(true)} className="btn-secondary">
            + Manual
          </button>
          <button
            onClick={startMining}
            disabled={mining}
            className="btn-brand"
          >
            {mining ? "Mining…" : "Mine my hooks"}
          </button>
        </div>
      </header>

      {latestRun ? <MiningStatus run={latestRun} /> : null}

      {clusters.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-500">Cluster:</span>
          <button
            onClick={() => setClusterFilter("")}
            className={`rounded-full px-3 py-1 text-xs ${
              !clusterFilter
                ? "bg-ink-900 text-white"
                : "bg-ink-100 text-ink-600 hover:bg-ink-200"
            }`}
          >
            All
          </button>
          {clusters.map((c) => (
            <button
              key={c}
              onClick={() => setClusterFilter(c)}
              className={`rounded-full px-3 py-1 text-xs ${
                clusterFilter === c
                  ? "bg-ink-900 text-white"
                  : "bg-ink-100 text-ink-600 hover:bg-ink-200"
              }`}
            >
              {CLUSTER_LABELS[c] ?? c}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-500">Loading…</p>
      ) : hooks.length === 0 ? (
        <EmptyState
          icon={<Anchor className="h-6 w-6" />}
          title="No hooks yet"
          description="Hooks are reusable opening templates extracted from your highest-performing past posts. Mine your library to find patterns that consistently win — you'll need a few published posts first."
          action={
            <button
              onClick={startMining}
              disabled={mining}
              className="btn-brand"
            >
              {mining ? "Mining…" : "Mine my hooks"}
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {hooks.map((h) => (
            <HookCard
              key={h.id}
              hook={h}
              onApply={() => setApplyingHook(h)}
              onEdit={() => setEditingHook(h)}
              onToggleStar={() => toggleStar(h)}
              onDelete={() => remove(h)}
            />
          ))}
        </div>
      )}

      {applyingHook ? (
        <ApplyModal
          hook={applyingHook}
          onClose={() => setApplyingHook(null)}
          onUsed={async () => {
            await load();
          }}
        />
      ) : null}
      {editingHook ? (
        <EditModal
          hook={editingHook}
          onClose={() => setEditingHook(null)}
          onSaved={async () => {
            setEditingHook(null);
            await load();
          }}
        />
      ) : null}
      {showCreate ? (
        <EditModal
          mode="create"
          onClose={() => setShowCreate(false)}
          onSaved={async () => {
            setShowCreate(false);
            await load();
          }}
        />
      ) : null}
    </div>
  );
}

function MiningStatus({ run }: { run: MiningRun }) {
  if (run.status === "pending") {
    return (
      <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
        Mining hooks from your published posts… this usually takes 10-30 seconds.
      </div>
    );
  }
  if (run.status === "failed") {
    return (
      <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
        Last mining run failed: {run.error_message ?? "unknown error"}
      </div>
    );
  }
  return (
    <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
      Last run: extracted <b>{run.hooks_added}</b> new hooks from{" "}
      <b>{run.source_posts_count}</b> posts.
    </div>
  );
}

function HookCard(props: {
  hook: Hook;
  onApply: () => void;
  onEdit: () => void;
  onToggleStar: () => void;
  onDelete: () => void;
}) {
  const h = props.hook;
  return (
    <div className="rounded-xl border border-ink-200/70 bg-white p-4 shadow-xs transition-all hover:-translate-y-0.5 hover:border-ink-200 hover:shadow-md">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-ink-900">{h.title}</h3>
            {h.cluster ? (
              <span
                className={`rounded-full px-2 py-0.5 text-[10px] ${
                  CLUSTER_COLOR[h.cluster] ?? "bg-ink-100 text-ink-700"
                }`}
              >
                {CLUSTER_LABELS[h.cluster] ?? h.cluster}
              </span>
            ) : null}
            <span className="text-[10px] text-ink-400">
              {h.source === "mined" ? "AI-mined" : h.source}
            </span>
          </div>
        </div>
        <button
          onClick={props.onToggleStar}
          className={`text-base ${h.starred ? "text-amber-500" : "text-ink-300 hover:text-amber-400"}`}
          title={h.starred ? "Unstar" : "Star"}
        >
          ★
        </button>
      </div>
      <p className="mb-2 whitespace-pre-wrap rounded bg-ink-50 px-2 py-1.5 font-mono text-xs text-ink-800">
        {h.template}
      </p>
      {h.example ? (
        <details className="mb-2">
          <summary className="cursor-pointer text-[11px] text-ink-500 hover:text-ink-800">
            Original example {h.source_likes != null ? `(${h.source_likes} likes)` : ""}
          </summary>
          <p className="mt-1 whitespace-pre-wrap rounded border border-ink-100 bg-white px-2 py-1.5 text-[11px] text-ink-700">
            {h.example}
          </p>
        </details>
      ) : null}
      <div className="flex items-center gap-3 text-xs">
        <button
          onClick={props.onApply}
          className="font-medium text-brand-700 hover:underline"
        >
          Apply →
        </button>
        <button onClick={props.onEdit} className="text-ink-700 hover:underline">
          Edit
        </button>
        <button onClick={props.onDelete} className="text-red-600 hover:underline">
          Delete
        </button>
        <span className="ml-auto text-ink-400">
          used {h.use_count}×
        </span>
      </div>
    </div>
  );
}

function ApplyModal(props: {
  hook: Hook;
  onClose: () => void;
  onUsed: () => void | Promise<void>;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState<"twitter" | "bluesky" | "linkedin">(
    "twitter"
  );
  const [voiceProfileId, setVoiceProfileId] = useState<string>("");
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [variants, setVariants] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [savingDraft, setSavingDraft] = useState<number | null>(null);

  useEffect(() => {
    api
      .get<{ voice_profiles: VoiceProfile[] }>("/api/voice-profiles")
      .then((r) =>
        setVoiceProfiles(
          (r.voice_profiles ?? []).filter((v) => v.status === "ready")
        )
      )
      .catch(() => setVoiceProfiles([]));
  }, []);

  async function generate() {
    if (!topic.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ variants: string[] }>(
        `/api/hooks/${props.hook.id}/apply`,
        {
          topic: topic.trim(),
          platform,
          voice_profile_id: voiceProfileId || null,
        }
      );
      setVariants(res.variants ?? []);
      await props.onUsed();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function copy(t: string) {
    navigator.clipboard
      .writeText(t)
      .then(() => toast.success("Copied to clipboard"))
      .catch(() => toast.error("Copy failed"));
  }

  async function saveAsDraft(text: string, idx: number) {
    setSavingDraft(idx);
    try {
      const res = await api.post<{ id: string }>("/api/drafts", {
        title: `From hook: ${props.hook.title}`,
        content: text,
        platform,
        hook_id: props.hook.id,
        voice_profile_id: voiceProfileId || null,
      });
      toast.success("Saved as draft.");
      navigate(`/app/drafts?id=${res.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingDraft(null);
    }
  }

  return (
    <Modal title={`Apply hook: ${props.hook.title}`} onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        <div className="rounded bg-ink-50 px-3 py-2 font-mono text-xs text-ink-700">
          {props.hook.template}
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Topic / idea</label>
          <textarea
            className="input w-full"
            rows={2}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. async-first remote teams, pricing experiments, Cloudflare D1 vs Postgres"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="mb-1 block text-ink-600">Platform</label>
            <select
              className="input w-full"
              value={platform}
              onChange={(e) =>
                setPlatform(e.target.value as "twitter" | "bluesky" | "linkedin")
              }
            >
              <option value="twitter">X / Twitter</option>
              <option value="bluesky">Bluesky</option>
              <option value="linkedin">LinkedIn</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-ink-600">Voice (optional)</label>
            <select
              className="input w-full"
              value={voiceProfileId}
              onChange={(e) => setVoiceProfileId(e.target.value)}
            >
              <option value="">No profile</option>
              {voiceProfiles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={props.onClose} className="btn-secondary">
            Close
          </button>
          <button
            onClick={generate}
            disabled={busy || !topic.trim()}
            className="btn-primary"
          >
            {busy ? "Generating…" : "Generate 3 variants"}
          </button>
        </div>

        {variants.length > 0 ? (
          <div className="mt-2 space-y-2 border-t border-ink-200 pt-3">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Variants
            </h4>
            {variants.map((v, i) => (
              <div
                key={i}
                className="rounded border border-ink-200 bg-white p-3 shadow-sm"
              >
                <p className="whitespace-pre-wrap text-ink-800">{v}</p>
                <div className="mt-2 flex items-center justify-end gap-3 text-xs">
                  <span className="mr-auto text-ink-400">{v.length} chars</span>
                  <button
                    onClick={() => copy(v)}
                    className="text-brand-700 hover:underline"
                  >
                    Copy
                  </button>
                  <button
                    onClick={() => saveAsDraft(v, i)}
                    disabled={savingDraft !== null}
                    className="font-medium text-brand-700 hover:underline disabled:text-ink-400"
                  >
                    {savingDraft === i ? "Saving…" : "Save as draft →"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function EditModal(props: {
  hook?: Hook;
  mode?: "create";
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const toast = useToast();
  const isCreate = props.mode === "create";
  const [title, setTitle] = useState(props.hook?.title ?? "");
  const [template, setTemplate] = useState(props.hook?.template ?? "");
  const [example, setExample] = useState(props.hook?.example ?? "");
  const [cluster, setCluster] = useState(props.hook?.cluster ?? "");
  const [busy, setBusy] = useState(false);

  async function save() {
    if (!title.trim() || !template.trim()) return;
    setBusy(true);
    try {
      if (isCreate) {
        await api.post("/api/hooks", {
          title: title.trim(),
          template: template.trim(),
          example: example.trim() || null,
          cluster: cluster || null,
        });
      } else if (props.hook) {
        await api.patch(`/api/hooks/${props.hook.id}`, {
          title: title.trim(),
          template: template.trim(),
          example: example.trim() || null,
          cluster: cluster || null,
        });
      }
      await props.onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title={isCreate ? "New hook" : "Edit hook"} onClose={props.onClose}>
      <div className="space-y-3 p-4 text-sm">
        <div>
          <label className="mb-1 block text-ink-600">Title</label>
          <input
            className="input w-full"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={100}
          />
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Template</label>
          <textarea
            className="input w-full font-mono text-xs"
            rows={3}
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            placeholder="Most {AUDIENCE} think {COMMON_BELIEF}. But..."
          />
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Example (optional)</label>
          <textarea
            className="input w-full text-xs"
            rows={3}
            value={example}
            onChange={(e) => setExample(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-ink-600">Cluster</label>
          <select
            className="input w-full"
            value={cluster}
            onChange={(e) => setCluster(e.target.value)}
          >
            <option value="">— none —</option>
            <option value="contrarian">Contrarian</option>
            <option value="listicle">Listicle</option>
            <option value="story">Story</option>
            <option value="question">Question</option>
            <option value="stat">Stat</option>
            <option value="declarative">Declarative</option>
            <option value="promise">Promise</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={props.onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy || !title.trim() || !template.trim()}
            className="btn-primary"
          >
            {busy ? "Saving…" : isCreate ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Modal(props: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm animate-fade-in"
      onClick={props.onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-ink-200/60 bg-white shadow-xl animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-4 py-3">
          <h3 className="text-base font-semibold text-ink-900">{props.title}</h3>
          <button
            onClick={props.onClose}
            className="text-ink-500 hover:text-ink-900"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {props.children}
      </div>
    </div>
  );
}
